import React, { useEffect, useState } from 'react';
import StatCard from '../../components/shared/StatsCard';
import { FaArrowLeft } from "react-icons/fa";
import { BiExport } from "react-icons/bi";
import { useLocation, useNavigate, useParams } from 'react-router';
import { CalendarIcon, PeopleIcon, AnalyticIcon } from '../../assets/icons/icons';
import ChartWrapper from '../../components/modules/analysis/ChartWrapper';
import IconButton from '../../components/shared/IconButton';
import { useSelector } from 'react-redux';
import { apiConnecter } from '../../services/apiConnector';
import exportToCSVRow from '../../services/exportToCSVRow';
import API_ENDPOINTS from '../../constant/EndPoints';

/**
 * @typedef {Object} EventState
 * @property {number} totalTaps - The total number of taps/clicks on the event URL.
 * @property {number} engagementRate - The event's engagement rate percentage.
 * @property {number} postClickRate - The post-event click rate percentage.
 * @property {number} attendance - The total number of expected/actual attendees.
 */

/**
 * @typedef {Object} AnalyticDataPoint
 * @property {string} timestamp - ISO date string of the click event.
 * // Other data points like userAgent, location, etc. would typically be here
 */

/**
 * @typedef {Object} FetchedAnalytics
 * @property {AnalyticDataPoint[]} analyticsData - Array of raw click data points.
 * @property {string} eventName - The name of the event, used for export file naming.
 */

/**
 * The main component for displaying detailed analytics for a single event ID.
 * It fetches initial summary data from router state and detailed click data from an API.
 *
 * @returns {JSX.Element} The Analytics dashboard UI.
 */
const Analytics = () => {
    /* ****************************************************************
    *                             ROUTER HOOKS                        *
    ******************************************************************/
    const { id } = useParams(); // Fetches the event ID from the URL
    const { state } = useLocation(); // Retrieves summary data passed during navigation
    const navigate = useNavigate();

    /* ****************************************************************
    *                             STATE                               *
    ******************************************************************/
    const userInfo = useSelector((state) => state.userInfo); // Get user token for API calls

    const [totalTaps, setTotaltaps] = useState();
    const [engagementRate, setengagementRate] = useState();
    const [postClick, setpostClick] = useState();
    const [attendance, setAttendance] = useState();
    const [postClickCount, setPostClickCount] = useState();
    const [analyticsData, setAnalyticsData] = useState(null);

    /* ****************************************************************
    *                             EFFECT HOOKS                        *
    ******************************************************************/

    // 3. Effect to initialize summary stats from navigation state
    useEffect(() => {
        // Initializes summary stats (total taps, rates, attendance) from the location state.
        if (state) {
            setTotaltaps(state?.totalTaps);
            setengagementRate(state?.engagementRate);
            setpostClick(state?.postClickRate);
            setAttendance(state?.attendance);            
            setPostClickCount(state?.postClickNum);
        }
    }, [state]);

    // 4. Effect to fetch detailed analytics when component mounts
    useEffect(() => {
        getClickAnalysis();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, userInfo.token]); // Dependency on 'id' and 'userInfo.token' ensures re-fetch if they change


    /* ****************************************************************
    *                            HELPER FUNCTION                        *
    ******************************************************************/
    /**
    
    * 
    * Calculates the number of click events that occurred within the last 24 hours.
    * @returns {number} The count of recent clicks.
    */
    const calculateLast24HoursClicks = () => {
        if (!analyticsData || !analyticsData.analyticsData) return 0;

        const currentTime = new Date().getTime();
        const last24Hours = 24 * 60 * 60 * 1000; // 86,400,000 milliseconds

        const eventsInLast24Hours = analyticsData.analyticsData.filter(event => {
            const eventTimestamp = new Date(event.timestamp).getTime();
            return (currentTime - eventTimestamp) <= last24Hours;
        });

        return eventsInLast24Hours.length;
    };

    /**
     * Exports the raw click analytics data to a CSV file.
     * Uses the eventName from the fetched analytics for the file name.
     */
    function handleExport() {
        if (analyticsData && analyticsData.analyticsData) {
            // 
            exportToCSVRow(analyticsData.analyticsData, analyticsData.eventName);
        } else {
            console.warn("No analytics data available to export.");
        }
    }


    /* ****************************************************************
    *                             SERVICES                            *
    ******************************************************************/
    /**
     * Fetches detailed click analytics data for the current event ID from the API.
     */
    const getClickAnalysis = async () => {
        // You might want to set a loading state here
        try {
            const response = await apiConnecter("GET",
                `${API_ENDPOINTS.REACT_APP_GET_CLICK_ANALYTICS_BY_ID_END_POINT}/${id}`,
                null,
                { authorization: `Bearer ${userInfo.token}` }
            );

            // Assuming the API structure is response.data.result
            if (response.data && response.data.result) {
                setAnalyticsData(response.data.result);
            }
        } catch (err) {
            console.error("Error fetching click analysis:", err?.response?.data?.message ?? err.message);
            // Implement visible error handling here
        } finally {
            // Unset loading state here
        }
    };



    // Calculate the 24-hour clicks *before* rendering
    const last24HoursClicks = calculateLast24HoursClicks();


    // 5. Render UI
    return (
        <div className='w-full m-5' >
            <div className="flex gap-2 flex-wrap justify-between mb-2">
                {/* Back Navigation and Title */}
                <h2 className="text-2xl font-semibold text-black flex gap-3 cursor-pointer">
                    <span className='mt-1' onClick={() => navigate(-1)}>
                        <FaArrowLeft />
                    </span>
                    Event Analytics
                </h2>

                {/* Export Button */}
                <IconButton
                    icon={BiExport}
                    label="Export"
                    onClick={handleExport}
                    hoverColor="hover:bg-gray-300"
                    bgColor="bg-[var(--analytics-card-color)]"
                    border={true}
                    disabled={analyticsData == null}
                    color='black'
                />
            </div>

            {/* Statistics Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-3">
                <StatCard
                    title="Total Taps"
                    value={totalTaps}
                    icon={CalendarIcon} // Placeholder icon
                    bgColor={'bg-[var(--analytics-card-color)]'}
                    textColor='black'
                    borderColor={'border-[var(--border-color2)]'}
                    description={`+${last24HoursClicks} in last 24h`}
                />
                <StatCard
                    title="Engagement Rate"
                    value={engagementRate ? engagementRate + "%" : '--'}
                    bgColor={'bg-[var(--analytics-card-color)]'}
                    textColor='black'
                    icon={PeopleIcon} // Placeholder icon
                    borderColor={'border-[var(--border-color2)]'}
                    description={`${totalTaps || 'N/A'} of ${attendance || 'N/A'} attendees`}
                />
                <StatCard
                    title="Post-Click Rate"
                    value={postClick ? postClick + "%" : '--'}
                    bgColor={'bg-[var(--analytics-card-color)]'}
                    textColor='black'
                    icon={AnalyticIcon} // Placeholder icon
                    borderColor={'border-[var(--border-color2)]'}
                    description={`Still engaging after event no of clicks ${postClickCount || 'N/A'}`}
                />
            </div>

            {/* Chart Visualization */}
            <ChartWrapper eventId={id} />
        </div>
    );
}

export default Analytics;
